/**
 * SpotlightPicker — compact popover for setting a job's highlight state.
 *
 * Replaces the previous single-click star toggle. Recruiters now pick:
 *   1. Highlighted on/off
 *   2. Colour (6 preset chips + custom hex)
 *   3. Spotlight until (optional expiry; +7/+14/+30 quick chips + date input)
 *
 * Fixes the "highlight is on but board renders in yellow / no colour"
 * bug — the old JobsList toggle only sent `{highlight: true}` and never
 * a colour, so the embed board fell back to a default.
 *
 * Also used by:
 *   - JobsList row star (via <SpotlightPickerTrigger>)
 *   - Job Detail (recruiter view) header badge → "Extend" workflow
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, Sparkles, Calendar as CalIcon } from "lucide-react";

const PRESET_COLORS = [
    { hex: "#FBBF24", name: "Amber (default)" },
    { hex: "#F97316", name: "Orange" },
    { hex: "#EF4444", name: "Rose" },
    { hex: "#8B5CF6", name: "Violet" },
    { hex: "#0EA5E9", name: "Sky" },
    { hex: "#10B981", name: "Emerald" },
];

function addDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

/**
 * Standalone picker UI (no trigger). Pass `job` + `onSaved` to embed in
 * a modal / inline. Otherwise use `<SpotlightPickerTrigger>` for the
 * common star-icon → popover pattern.
 */
export function SpotlightPickerBody({ job, onSaved, onCancel, dense = false }) {
    const [highlight, setHighlight] = useState(!!job?.highlight);
    const [color, setColor] = useState(job?.highlight_color || "#FBBF24");
    const [until, setUntil] = useState(() => (job?.spotlight_until || "").slice(0, 10));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                highlight,
                highlight_color: highlight ? color : null,
                // Store as ISO datetime at end-of-day so the auto-expiry
                // rule (spotlight_until < now) doesn't kill a same-day
                // highlight the moment the recruiter saves it.
                spotlight_until: highlight && until ? `${until}T23:59:59+00:00` : null,
            };
            await api.patch(`/jobs/${job.id}`, payload);
            toast.success(highlight ? "Spotlight updated" : "Spotlight removed");
            onSaved?.(payload);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className={`space-y-3 ${dense ? "text-xs" : "text-sm"}`} data-testid="spotlight-picker">
            <div className="flex items-center justify-between gap-2">
                <div className="font-display font-bold text-base">Spotlight settings</div>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={highlight}
                        onChange={(e) => setHighlight(e.target.checked)}
                        data-testid="spotlight-highlight-toggle"
                    />
                    <span className="text-xs">Highlighted</span>
                </label>
            </div>

            <div className={highlight ? "space-y-3" : "space-y-3 opacity-40 pointer-events-none"}>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Colour</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {PRESET_COLORS.map((c) => (
                            <button
                                key={c.hex}
                                type="button"
                                onClick={() => setColor(c.hex)}
                                className={`w-7 h-7 border-2 ${color.toLowerCase() === c.hex.toLowerCase() ? "border-foreground scale-110" : "border-border"} transition-transform`}
                                style={{ background: c.hex }}
                                title={c.name}
                                aria-label={c.name}
                                data-testid={`spotlight-color-${c.hex.replace("#", "")}`}
                            />
                        ))}
                        <Input
                            type="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="w-8 h-8 p-0 rounded-none border-2 border-border cursor-pointer"
                            title="Custom hex"
                            data-testid="spotlight-color-custom"
                        />
                    </div>
                </div>

                <div>
                    <Label className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Spotlight until <span className="normal-case text-foreground/60">(optional — auto-expires after this date)</span></Label>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Input
                            type="date"
                            value={until}
                            onChange={(e) => setUntil(e.target.value)}
                            className="rounded-none h-8 w-40 text-xs"
                            data-testid="spotlight-until-input"
                        />
                        {[7, 14, 30].map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => setUntil(addDays(d))}
                                className="h-8 px-2 border border-border bg-white hover:bg-secondary text-[10px] font-mono uppercase tracking-widest"
                                data-testid={`spotlight-until-plus-${d}`}
                            >
                                +{d}d
                            </button>
                        ))}
                        {until && (
                            <button
                                type="button"
                                onClick={() => setUntil("")}
                                className="h-8 px-2 text-[10px] text-muted-foreground hover:text-rose-700 underline"
                            >Clear</button>
                        )}
                    </div>
                </div>

                {/* Preview */}
                <div className="border p-2" style={{ background: `${color}20`, borderColor: color }}>
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color }}>★ Featured · preview</div>
                    <div className="text-sm font-semibold mt-0.5">{job?.title || "Job title"}</div>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
                {onCancel && (
                    <Button variant="outline" onClick={onCancel} className="rounded-none h-8 text-xs">Cancel</Button>
                )}
                <Button
                    onClick={save}
                    disabled={saving}
                    className="rounded-none h-8 text-xs"
                    data-testid="spotlight-save-btn"
                >
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    {saving ? "Saving…" : "Save spotlight"}
                </Button>
            </div>
        </div>
    );
}

/**
 * Star-button trigger + popover — drop-in replacement for the old
 * single-click star toggle in the JobsList table.
 */
export function SpotlightPickerTrigger({ job, onSaved, size = 3.5 }) {
    const [open, setOpen] = useState(false);
    const on = !!job.highlight;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center justify-center w-6 h-6 border transition-colors ${on ? "text-amber-700 bg-amber-50 hover:bg-amber-100" : "border-border text-foreground/30 bg-white hover:border-amber-300 hover:text-amber-500"}`}
                    style={on ? { borderColor: job.highlight_color || "#FBBF24" } : undefined}
                    data-testid={`job-promo-toggle-${job.id}`}
                    title={on ? "Edit spotlight settings" : "Promote this job (embed board spotlight)"}
                >
                    <Star className={`w-${size} h-${size} ${on ? "fill-current" : ""}`} style={on ? { color: job.highlight_color || "#FBBF24" } : undefined} />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-4" align="start" onClick={(e) => e.stopPropagation()}>
                <SpotlightPickerBody
                    job={job}
                    onSaved={(payload) => {
                        onSaved?.(payload);
                        setOpen(false);
                    }}
                    onCancel={() => setOpen(false)}
                />
            </PopoverContent>
        </Popover>
    );
}
